import { User, NewUser, UserRole } from '../../types';
import api from '../api';

export class UsersService {
  static async getUsers(token?: string): Promise<User[]> {
    // TEMPORARY: Using fresh-data endpoint to bypass API Gateway cache
    // TODO: Revert to '/api/users' once cache issue is resolved
    const response = await api.get<any>('/api/users/fresh-data', token);
    return response.users || [];
  }

  static async getUserById(id: number, token?: string): Promise<User> {
    return await api.get<User>(`/api/users/${id}`, token);
  }

  static async createUser(userData: NewUser, token?: string): Promise<User> {
    return await api.post<User>('/api/users', userData, token);
  }

  static async updateUser(id: number, userData: Partial<User>, token?: string): Promise<User> {
    return await api.put<User>(`/api/users/${id}`, userData, token);
  }

  static async deleteUser(id: number, token?: string): Promise<void> {
    await api.delete(`/api/users/${id}`, token);
  }

  static async uploadProfileImage(id: number, imageFile: File, token?: string): Promise<{ profile_image_url: string; message: string }> {
    const formData = new FormData();
    formData.append('profile_image', imageFile);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${id}/profile-image`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to upload profile image');
    }

    return await response.json();
  }

  static async getRoles(token?: string): Promise<UserRole[]> {
    return await api.get<UserRole[]>('/api/users/roles/all', token);
  }

  static async assignRole(userId: number, roleId: number, token?: string): Promise<void> {
    await api.post(`/api/users/${userId}/roles`, { role_id: roleId }, token);
  }

  static async removeRole(userId: number, roleId: number, token?: string): Promise<void> {
    await api.delete(`/api/users/${userId}/roles/${roleId}`, token);
  }

  // Client Assignment Methods
  static async assignUserToClient(userId: number, clientId: number, role: string = 'contact', hourlyRate?: number, token?: string): Promise<void> {
    await api.post(`/api/users/${userId}/clients`, { client_id: clientId, role, hourly_rate: hourlyRate }, token);
  }

  static async removeUserFromClient(userId: number, clientId: number, token?: string): Promise<void> {
    await api.delete(`/api/users/${userId}/clients/${clientId}`, token);
  }

  // Project Assignment Methods
  static async assignUserToProject(userId: number, projectId: number, role: string = 'designer', hourlyRate?: number, token?: string): Promise<void> {
    await api.post(`/api/users/${userId}/projects`, { project_id: projectId, role, hourly_rate: hourlyRate }, token);
  }

  static async removeUserFromProject(userId: number, projectId: number, token?: string): Promise<void> {
    await api.delete(`/api/users/${userId}/projects/${projectId}`, token);
  }
}